import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useStatus } from "@powersync/react";
import type { PairedKdsDevice } from "../lib/kdsDevice";
import { TicketCard } from "./TicketCard";
import { useKitchenTickets, type SourceFilter, type Ticket } from "../lib/useKitchenTickets";
import { useIsWide, useNow } from "../lib/useClock";
import { ageSeconds, fmtAge, formatClock } from "../lib/format";
import { alertsOff, checkNotificationStatus, getNotificationStatus, playSound, setAlertsOff, requestNotificationPermission, subscribeToPush, unlockAudio } from "../lib/notify";

const Icon = ({ name, size = 22, color }: { name: string; size?: number; color?: string }) => (
  <span className="ms" aria-hidden="true" style={{ fontSize: size, color }}>
    {name}
  </span>
);

const COLUMNS: Array<{ key: Ticket["kitchenStatus"]; label: string; icon: string; color: string; empty: [string, string] }> = [
  { key: "new", label: "New", icon: "fiber_new", color: "var(--ink)", empty: ["inbox", "No new tickets"] },
  { key: "preparing", label: "Cooking", icon: "skillet", color: "var(--amber)", empty: ["restaurant", "Nothing cooking"] },
  { key: "ready", label: "Ready", icon: "check_circle", color: "var(--ok)", empty: ["done_all", "Nothing waiting"] },
];
/**
 * A column's scrolling list. A ticket that just arrived is the youngest, so it lands at the bottom and
 * can be below the fold: then a pill at the column's foot says so, and a tap scrolls to it
 * (canvas v2-k-board, v2-km-new). The pill goes once the ticket's strip is on screen.
 */
function ColumnBody({ fresh, children }: { fresh: string[]; children: ReactNode }) {
  const body = useRef<HTMLDivElement>(null);
  const [below, setBelow] = useState<HTMLElement[]>([]);
  const key = fresh.join();
  useEffect(() => {
    const el = body.current;
    if (!el || !key) return setBelow([]);
    const measure = () => {
      const floor = el.getBoundingClientRect().bottom - 40; // the strip is 34px tall
      const hidden = key.split(",").map((id) => el.querySelector<HTMLElement>(`[data-ticket="${id}"]`)).filter((t): t is HTMLElement => !!t && t.getBoundingClientRect().top > floor);
      setBelow((prev) => (prev.length === hidden.length && prev.every((t, i) => t === hidden[i]) ? prev : hidden));
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    return () => el.removeEventListener("scroll", measure);
  }, [key]);
  return (
    <>
      <div className="kds-col-body" ref={body}>
        {children}
      </div>
      {below.length > 0 && (
        <button type="button" className="kds-new-below" onClick={() => below[0]!.scrollIntoView({ behavior: "smooth", block: "nearest" })}>
          <i aria-hidden="true" />
          {below.length} new {below.length === 1 ? "order" : "orders"}
          <Icon name="arrow_downward" size={20} />
        </button>
      )}
    </>
  );
}

const FILTERS: Array<[SourceFilter, string]> = [
  ["all", "All"],
  ["dine-in", "Dine-in"],
  ["pickup", "Pickup"],
];

export function KitchenBoard({ device, onUnpair }: { device: PairedKdsDevice; onUnpair: () => void }) {
  const [source, setSource] = useState<SourceFilter>("all");
  const [column, setColumn] = useState<Ticket["kitchenStatus"]>("new");
  const [confirmUnpair, setConfirmUnpair] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [askAlerts, setAskAlerts] = useState(false);
  const now = useNow();
  const isWide = useIsWide(880);
  const { tickets, advance, recall, toggleItem, ready } = useKitchenTickets(device.storeId);
  // The board opens as it was last time; until this load's first sync lands, a pill says so.
  const { lastSyncedAt } = useStatus();
  const [openedAt] = useState(() => Date.now());
  const catchingUp = !lastSyncedAt || lastSyncedAt.getTime() < openedAt;

  // Alerts: a chime plus a push notification per new ticket, once the browser allows them.
  const [notif, setNotif] = useState(() => getNotificationStatus());
  const [off, setOff] = useState(alertsOff); // turned off on this display, though allowed
  const on = notif === "granted" && !off;
  const toggleAlerts = () => {
    setOff(!off);
    void setAlertsOff(!off, device.deviceId);
  };
  // Right after pairing, the alerts prompt opens once by itself (per pairing): a kitchen screen
  // without alerts is easy to miss, and asking with the reason beats the bare system prompt.
  useEffect(() => {
    void checkNotificationStatus().then((status) => {
      setNotif(status);
      const key = `cliqsuite_kds_alerts_asked_${device.deviceId}`;
      try {
        if (status !== "default" || localStorage.getItem(key)) return;
        localStorage.setItem(key, "1");
      } catch {
        return;
      }
      setAskAlerts(true);
    });
  }, [device.deviceId]);
  useEffect(() => {
    if (notif === "granted" && !off) void subscribeToPush(device.deviceId);
  }, [device.deviceId, notif, off]);
  const enableAlerts = () => {
    unlockAudio();
    void requestNotificationPermission().then(setNotif);
  };

  // New arrivals chime and glow for 8 seconds. The board's first load isn't "new".
  const [justIn, setJustIn] = useState<Set<string>>(new Set());
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!ready) return; // still loading: what turns up next is the board's first load, not an arrival
    const ids = new Set(tickets.map((t) => t.id));
    const before = seen.current;
    seen.current = ids;
    if (!before) return;
    const arrived = tickets.filter((t) => !before.has(t.id)).map((t) => t.id);
    if (!arrived.length) return;
    if (!off) playSound("/sounds/new-ticket.mp3");
    setJustIn((prev) => new Set([...prev, ...arrived]));
    setTimeout(() => setJustIn((prev) => new Set([...prev].filter((id) => !arrived.includes(id)))), 8000);
  }, [tickets, ready]);

  const shown = useMemo(() => tickets.filter((t) => source === "all" || (source === "dine-in") === t.isDineIn), [tickets, source]);
  const ages = shown.filter((t) => t.kitchenStatus !== "ready").map((t) => ageSeconds(t.createdAt, now));
  const clock = formatClock(now);
  const count = (f: SourceFilter) => tickets.filter((t) => f === "all" || (f === "dine-in") === t.isDineIn).length;

  return (
    <div className="kds cq-full-height">
      <header className="kds-top">
        <div className="kds-brand">
          <img className="kds-brand-mark" src="/kds-mark.svg" alt="" />
          <span>
            <b>Kitchen</b>
            {isWide && <small>{device.registerName}, {device.storeName}</small>}
          </span>
        </div>
        {isWide ? (
          <>
            <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <div className="kds-tabs" role="tablist" aria-label="Order type">
                {FILTERS.map(([key, label]) => (
                  <button key={key} role="tab" aria-selected={source === key} onClick={() => setSource(key)}>
                    {label}
                    <span className="n mono">{count(key)}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="kds-stat">
              <small>Avg wait</small>
              <b className="mono">{fmtAge(ages.length ? ages.reduce((a, b) => a + b, 0) / ages.length : 0)}</b>
            </div>
            <div className="kds-stat">
              <small>Oldest</small>
              <b className="mono">{fmtAge(ages.length ? Math.max(...ages) : 0)}</b>
            </div>
            <div className="kds-clock mono">
              {clock.time}
              <span> {clock.ap}</span>
            </div>
          </>
        ) : (
          <>
            <div className="kds-clock mono">{clock.time}</div>
            {/* the phone's order-type filter sits behind a button (canvas v2-km-filter); a dot while it's narrowed */}
            <button className={`kds-icon-btn${source === "all" ? "" : " is-on"}`} onClick={() => setFilterOpen(true)} aria-label={`Order type: ${FILTERS.find(([k]) => k === source)![1]}`} aria-haspopup="menu">
              <Icon name="filter_list" size={21} />
            </button>
          </>
        )}
        {notif !== "unsupported" && (
          <button className="kds-icon-btn" onClick={() => (notif === "default" ? setAskAlerts(true) : notif === "granted" ? toggleAlerts() : enableAlerts())} aria-label={on ? "Alerts on, tap to turn off" : "Alerts off"} title={notif === "denied" ? "Blocked in this browser's settings" : undefined}>
            <Icon name={on ? "notifications_active" : "notifications_off"} size={23} />
          </button>
        )}
        <button className="kds-icon-btn" onClick={() => setConfirmUnpair(true)} aria-label="Unpair this display">
          <Icon name="link_off" />
        </button>
        {!isWide && (
          <div className="kds-tabs kds-tabs--cols" role="tablist" aria-label="Column">
            {COLUMNS.map((c) => (
              <button key={c.key} role="tab" aria-selected={column === c.key} onClick={() => setColumn(c.key)}>
                {c.label}
                <span className="n mono">{shown.filter((t) => t.kitchenStatus === c.key).length}</span>
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="kds-sheet">
        {!ready ? (
          <div className="kds-loading">Loading tickets…</div>
        ) : (
          <>
            <div className="kds-cols">
              {COLUMNS.filter((c) => isWide || c.key === column).map((c) => {
                const list = shown.filter((t) => t.kitchenStatus === c.key);
                return (
                  <section key={c.key} className="kds-col" aria-label={c.label}>
                    {isWide && (
                      <div className="kds-col-head">
                        <Icon name={c.icon} color={c.color} />
                        <h2>{c.label}</h2>
                        <span className="kds-count mono">{list.length}</span>
                      </div>
                    )}
                    <ColumnBody fresh={list.filter((t) => justIn.has(t.id)).map((t) => t.id)}>
                      {list.length ? (
                        list.map((t) => <TicketCard key={t.id} ticket={t} now={now} justArrived={justIn.has(t.id)} onBump={advance} onRecall={recall} onToggleItem={toggleItem} />)
                      ) : (
                        <div className="kds-empty">
                          <Icon name={c.empty[0]} size={38} color="#C9CCD3" />
                          {c.empty[1]}
                        </div>
                      )}
                    </ColumnBody>
                  </section>
                );
              })}
            </div>
          </>
        )}
        {ready && catchingUp && (
          <div className="kds-catchup" role="status">
            <Icon name="sync" size={22} color="var(--coral)" />
            Getting today&rsquo;s tickets
          </div>
        )}
        {notif === "default" && (
          <div className="kds-bar" role="status">
            <Icon name="notifications_off" size={21} color="#9A6B05" />
            Alerts are off. New tickets won&rsquo;t chime.
            <button onClick={() => setAskAlerts(true)}>Enable alerts</button>
          </div>
        )}
      </main>

      {askAlerts && (
        <div className="dlg-back" onClick={() => setAskAlerts(false)}>
          <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="alerts-title" onClick={(e) => e.stopPropagation()}>
            <span style={{ width: 52, height: 52, borderRadius: 16, background: "var(--amber-soft)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="notifications_active" size={26} color="var(--amber)" />
            </span>
            <h2 id="alerts-title">Turn on kitchen alerts</h2>
            <p>A chime and a notification for every new ticket, even when this screen is in the background. You'll be asked to allow notifications.</p>
            <div className="dlg-row">
              <button style={{ background: "var(--sheet)", color: "var(--ink)" }} onClick={() => setAskAlerts(false)}>
                Cancel
              </button>
              <button style={{ background: "var(--ink)", color: "#fff" }} onClick={() => { setAskAlerts(false); enableAlerts(); }}>
                Allow alerts
              </button>
            </div>
          </div>
        </div>
      )}

      {filterOpen && (
        <div className="kds-menu-back" onClick={() => setFilterOpen(false)}>
          <div className="kds-menu" role="menu" aria-label="Order type" onClick={(e) => e.stopPropagation()}>
            <small>Show orders</small>
            {FILTERS.map(([key, label]) => (
              <button key={key} role="menuitemradio" aria-checked={source === key} onClick={() => { setSource(key); setFilterOpen(false); }}>
                {source === key ? <Icon name="check_circle" color="var(--coral)" /> : <i />}
                <span className="l">{label}</span>
                <span className="n mono">{count(key)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {confirmUnpair && (
        <div className="dlg-back" onClick={() => setConfirmUnpair(false)}>
          <div className="dlg" role="alertdialog" aria-modal="true" aria-labelledby="unpair-title" onClick={(e) => e.stopPropagation()}>
            <Icon name="link_off" size={28} color="var(--coral)" />
            <h2 id="unpair-title">Unpair this display?</h2>
            <p>Tickets stop showing here until it&rsquo;s paired again with a new code from the Business app, under Devices.</p>
            <div className="dlg-row">
              <button style={{ background: "var(--sheet)", color: "var(--ink)" }} onClick={() => setConfirmUnpair(false)}>
                Keep paired
              </button>
              <button style={{ background: "var(--coral)", color: "#fff" }} onClick={() => { setConfirmUnpair(false); onUnpair(); }}>
                Unpair
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
